import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../context/AuthContext';
import { 
  ChevronLeft, 
  ChevronRight, 
  Calendar as CalendarIcon, 
  Video, 
  Phone, 
  MapPin, 
  User, 
  ExternalLink, 
  Download, 
  CalendarPlus,
  Clock,
  Briefcase,
  Loader2,
  CheckCircle,
  AlertCircle,
  Lock
} from 'lucide-react';
import { 
  generateGoogleCalendarUrl, 
  downloadICSFile, 
  generateICSContent, 
  formatEventDateTime,
  checkBookingExpired 
} from '../../utils/calendarUtils';

const TIME_SLOTS = [
  '9:00 AM - 10:00 AM',
  '10:00 AM - 11:00 AM',
  '11:00 AM - 12:00 PM',
  '1:00 PM - 2:00 PM',
  '2:00 PM - 3:00 PM',
  '3:00 PM - 4:00 PM',
];

export default function Calendar() {
  const { user, profile } = useAuth();
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [loading, setLoading] = useState(true);
  const [events, setEvents] = useState([]);
  const [error, setError] = useState(null);

  // Strategy session booking states
  const [requesting, setRequesting] = useState(false);
  const [activeBooking, setActiveBooking] = useState(null);
  const [pastBookings, setPastBookings] = useState([]);
  const [preferredDate, setPreferredDate] = useState('');
  const [preferredTime, setPreferredTime] = useState('');
  const [topic, setTopic] = useState('');
  const [bookingNotification, setBookingNotification] = useState({ type: '', text: '' });
  const hasFetchedRef = useRef(false);

  const today = new Date();
  const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

  useEffect(() => {
    if (user && !hasFetchedRef.current) {
      hasFetchedRef.current = true;
      fetchEvents();
    }
  }, [user]);

  // Clean time strings (like '9:00 AM - 10:00 AM') into clean 'HH:MM' for calendar template compatibility
  const cleanTimeForCalendar = (timeStr) => {
    if (!timeStr) return '12:00';
    if (/^\d{2}:\d{2}$/.test(timeStr)) {
      return timeStr;
    }
    const match = timeStr.match(/(\d{1,2}):(\d{2})\s*(AM|PM)?/i);
    if (match) {
      let hours = parseInt(match[1], 10);
      const minutes = match[2];
      const ampm = match[3] ? match[3].toUpperCase() : null;
      
      if (ampm === 'PM' && hours < 12) {
        hours += 12;
      } else if (ampm === 'AM' && hours === 12) {
        hours = 0;
      }
      return `${String(hours).padStart(2, '0')}:${minutes}`;
    }
    return '12:00';
  };

  const fetchEvents = async () => {
    if (events.length === 0) setLoading(true);
    setError(null);
    try {
      const fetchedEvents = [];

      // 1. Fetch applications with interview status
      const { data: appData, error: appError } = await supabase
        .from('applications')
        .select('*')
        .eq('user_id', user.id)
        .eq('status', 'Interview');

      if (appError && appError.code !== '42P01') {
        throw appError;
      }

      if (appData) {
        appData.forEach(app => {
          if (app.interview_date) {
            fetchedEvents.push({
              id: `interview-${app.id}`,
              type: 'interview',
              title: `Interview: ${app.role} at ${app.company}`,
              company: app.company,
              role: app.role,
              date: app.interview_date, // YYYY-MM-DD
              time: app.interview_time || '10:00',
              location: app.interview_link || app.location || 'Video Call Link Provided in Invitation',
              notes: app.interview_notes || 'Strategic alignment interview.',
              status: 'scheduled',
              raw: app
            });
          }
        });
      }

      // 2. Fetch strategist coaching bookings
      const { data: bookingData, error: bookingError } = await supabase
        .from('bookings')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (bookingError && bookingError.code !== '42P01') {
        throw bookingError;
      }

      if (bookingData) {
        // Find active booking (approved or non-expired pending)
        const active = bookingData.find(b => {
          if (b.status === 'approved') return true;
          if (b.status === 'pending') return !checkBookingExpired(b);
          return false;
        });

        // Find past/inactive bookings (completed, rejected, expired, or expired pending)
        const past = bookingData.filter(b => {
          if (active && b.id === active.id) return false;
          return b.status === 'completed' || b.status === 'rejected' || b.status === 'expired' || b.status === 'pending';
        });

        setActiveBooking(active || null);
        setPastBookings(past);

        bookingData.forEach(book => {
          if (book.status !== 'cancelled' && book.status !== 'rejected') {
            const bDate = book.scheduled_date || book.preferred_date;
            const bTime = book.scheduled_time || book.preferred_time;
            if (bDate) {
              fetchedEvents.push({
                id: `booking-${book.id}`,
                type: 'booking',
                title: `Coaching: ${book.topic || 'Strategy Session'}`,
                date: bDate, // YYYY-MM-DD
                time: bTime || '12:00',
                location: book.meeting_link || 'Google Meet (See invite details)',
                notes: book.admin_note || 'One-on-one session with your dedicated strategist.',
                status: book.status,
                raw: book
              });
            }
          }
        });
      }

      setEvents(fetchedEvents);
    } catch (err) {
      console.error('Error fetching calendar events:', err);
      setError('Could not retrieve calendar items.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitBooking = async (e) => {
    e.preventDefault();
    if (!preferredDate || !preferredTime) return;

    setRequesting(true);
    setBookingNotification({ type: '', text: '' });
    try {
      const { error } = await supabase
        .from('bookings')
        .insert({
          user_id: user.id,
          preferred_date: preferredDate,
          preferred_time: preferredTime,
          topic: topic.trim() || null,
        });

      if (error) throw error;
      setBookingNotification({ type: 'success', text: 'Booking request submitted successfully!' });
      
      // Send push notification to admins
      const senderName = profile ? `${profile.first_name} ${profile.last_name}` : (user.email || 'A client');
      supabase.functions.invoke('send-push', {
        body: {
          target_role: 'admin',
          title: 'New Booking Request 📅',
          body: `${senderName} has requested a session on ${preferredDate} at ${preferredTime}.`,
          url: '/admin/bookings'
        }
      }).catch(e => console.error('Error triggering push to admin:', e));

      setPreferredDate('');
      setPreferredTime('');
      setTopic('');
      await fetchEvents();
      setTimeout(() => {
        setBookingNotification(prev => prev.text === 'Booking request submitted successfully!' ? { type: '', text: '' } : prev);
      }, 5000);
    } catch (err) {
      console.error(err);
      setBookingNotification({ type: 'error', text: 'Failed to submit booking. Please try again.' });
      setTimeout(() => {
        setBookingNotification(prev => prev.text === 'Failed to submit booking. Please try again.' ? { type: '', text: '' } : prev);
      }, 5000);
    } finally {
      setRequesting(false);
    }
  };

  // Monthly navigation handlers
  const handlePrevMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  };

  const handleNextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  };

  // Calendar logic helpers
  const year = currentMonth.getFullYear();
  const month = currentMonth.getMonth();

  const firstDayOfMonth = new Date(year, month, 1);
  const startDayOfWeek = firstDayOfMonth.getDay();
  const totalDaysInMonth = new Date(year, month + 1, 0).getDate();
  const totalDaysInPrevMonth = new Date(year, month, 0).getDate();

  const calendarDays = [];

  // Add previous month filler days
  for (let i = startDayOfWeek - 1; i >= 0; i--) {
    calendarDays.push({
      date: new Date(year, month - 1, totalDaysInPrevMonth - i),
      isCurrentMonth: false,
    });
  }

  // Add current month days
  for (let i = 1; i <= totalDaysInMonth; i++) {
    calendarDays.push({
      date: new Date(year, month, i),
      isCurrentMonth: true,
    });
  }

  // Add next month filler days to complete grid
  const remainingCells = 42 - calendarDays.length;
  for (let i = 1; i <= remainingCells; i++) {
    calendarDays.push({
      date: new Date(year, month + 1, i),
      isCurrentMonth: false,
    });
  }

  // Check if dates are equivalent
  const isSameDay = (d1, d2) => {
    return d1.getFullYear() === d2.getFullYear() &&
           d1.getMonth() === d2.getMonth() &&
           d1.getDate() === d2.getDate();
  };

  // Format date to ISO key YYYY-MM-DD
  const getISODateString = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };

  // Get events on a specific day
  const getEventsForDay = (dateObj) => {
    const dateStr = getISODateString(dateObj);
    return events.filter(e => e.date === dateStr);
  };

  const selectedDateEvents = getEventsForDay(selectedDate);

  // Sorting future events for sidebar
  const getUpcomingEvents = () => {
    const todayStr = getISODateString(new Date());
    return events
      .filter(e => e.date >= todayStr)
      .sort((a, b) => new Date(`${a.date}T${cleanTimeForCalendar(a.time)}`) - new Date(`${b.date}T${cleanTimeForCalendar(b.time)}`))
      .slice(0, 5);
  };

  const upcomingEvents = getUpcomingEvents();

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending': return { bg: 'bg-amber-100 text-amber-700', label: 'Pending' };
      case 'approved': return { bg: 'bg-green-100 text-green-700', label: 'Approved' };
      case 'rejected': return { bg: 'bg-red-100 text-red-700', label: 'Rejected' };
      case 'completed': return { bg: 'bg-slate-100 text-slate-600', label: 'Completed' };
      case 'expired': return { bg: 'bg-orange-100 text-orange-700', label: 'Expired' };
      default: return { bg: 'bg-slate-100 text-slate-600', label: status };
    }
  };

  if (loading && events.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[500px]">
        <Loader2 className="w-10 h-10 animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto py-8 px-4 sm:px-6 lg:px-8 space-y-8">
      {/* Page Header */}
      <div>
        <h1 className="text-3xl font-extrabold text-slate-900 tracking-tight">Your Career Calendar</h1>
        <p className="mt-2 text-sm text-slate-500">
          Track interviews, mentoring check-ins, and strategic consultation sessions in one premium workspace.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Calendar Section (Left 2 cols on desktop) */}
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6 space-y-6">
            
            {/* Calendar Controls */}
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-bold text-slate-800">
                {currentMonth.toLocaleDateString('en-NG', { month: 'long', year: 'numeric' })}
              </h2>
              <div className="flex items-center gap-2">
                <button
                  onClick={handlePrevMonth}
                  className="p-2 border border-slate-200 rounded-lg text-slate-600 hover:bg-slate-50 transition-colors"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <button
                  onClick={() => setCurrentMonth(new Date())}
                  className="px-3 py-2 border border-slate-200 rounded-lg text-xs font-semibold text-slate-700 hover:bg-slate-50 transition-colors"
                >
                  Today
                </button>
                <button
                  onClick={handleNextMonth}
                  className="p-2 border border-slate-200 rounded-lg text-slate-600 hover:bg-slate-50 transition-colors"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Grid Header (Days of week) */}
            <div className="grid grid-cols-7 text-center text-xs font-bold text-slate-400 border-b border-slate-100 pb-3">
              <div>Sun</div>
              <div>Mon</div>
              <div>Tue</div>
              <div>Wed</div>
              <div>Thu</div>
              <div>Fri</div>
              <div>Sat</div>
            </div>

            {/* Calendar Days */}
            <div className="grid grid-cols-7 gap-y-2 gap-x-2">
              {calendarDays.map((cell, index) => {
                const dayEvents = getEventsForDay(cell.date);
                const isSelected = isSameDay(cell.date, selectedDate);
                const isToday = isSameDay(cell.date, new Date());
                
                return (
                  <button
                    key={index}
                    onClick={() => setSelectedDate(cell.date)}
                    className={`
                      aspect-square rounded-xl p-1 flex flex-col justify-between items-center transition-all relative group
                      ${cell.isCurrentMonth ? 'text-slate-800' : 'text-slate-300'}
                      ${isSelected ? 'bg-indigo-600 text-white font-bold shadow-md shadow-indigo-100' : 'hover:bg-slate-50'}
                      ${isToday && !isSelected ? 'ring-2 ring-indigo-600 ring-offset-2 font-bold' : ''}
                    `}
                  >
                    <span className="text-sm">{cell.date.getDate()}</span>
                    
                    {/* Event indicator (Single Red Dot) */}
                    {dayEvents.length > 0 && (
                      <div className="flex justify-center mt-1 w-full">
                        <span
                          className={`
                            w-1.5 h-1.5 rounded-full 
                            ${isSelected ? 'bg-red-200' : 'bg-red-500'}
                          `}
                        />
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Selected Date Events */}
          <div className="space-y-4">
            <h3 className="text-lg font-bold text-slate-800">
              Schedule for {selectedDate.toLocaleDateString('en-NG', { weekday: 'long', month: 'short', day: 'numeric' })}
            </h3>

            {selectedDateEvents.length === 0 ? (
              <div className="bg-slate-50 border border-dashed border-slate-200 rounded-2xl p-8 text-center text-slate-500">
                <CalendarIcon className="w-10 h-10 mx-auto text-slate-300 mb-2" />
                <p className="text-sm">No events scheduled for this day.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {selectedDateEvents.map(evt => (
                  <div 
                    key={evt.id} 
                    className={`
                      p-5 rounded-2xl border bg-white shadow-sm flex flex-col sm:flex-row sm:items-center justify-between gap-4 transition-all hover:shadow-md
                      ${evt.type === 'interview' ? 'border-purple-100 hover:border-purple-200' : 'border-green-100 hover:border-green-200'}
                    `}
                  >
                    <div className="flex items-start gap-4">
                      <div className={`p-3 rounded-xl ${evt.type === 'interview' ? 'bg-purple-50 text-purple-600' : 'bg-green-50 text-green-600'}`}>
                        {evt.type === 'interview' ? <Briefcase className="w-6 h-6" /> : <Video className="w-6 h-6" />}
                      </div>
                      <div className="space-y-1">
                        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full inline-block ${
                          evt.type === 'interview' 
                            ? 'bg-purple-100 text-purple-800' 
                            : evt.status === 'pending'
                              ? 'bg-amber-100 text-amber-800'
                              : 'bg-green-100 text-green-800'
                        }`}>
                          {evt.type === 'interview' 
                            ? 'Interview Session' 
                            : `Coaching Session (${evt.status === 'pending' ? 'Pending' : 'Confirmed'})`}
                        </span>
                        <h4 className="font-bold text-slate-900 text-base">{evt.title}</h4>
                        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-slate-500">
                          <span className="flex items-center gap-1"><Clock className="w-4 h-4" /> {formatEventDateTime(evt.date, cleanTimeForCalendar(evt.time))}</span>
                          <span className="flex items-center gap-1">
                            {evt.location.startsWith('http') ? (
                              <a href={evt.location} target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:underline inline-flex items-center gap-0.5">
                                Join Call <ExternalLink className="w-3.5 h-3.5" />
                              </a>
                            ) : (
                              <><MapPin className="w-4 h-4" /> {evt.location}</>
                            )}
                          </span>
                        </div>
                        {evt.notes && <p className="text-xs text-slate-400 mt-2 max-w-xl italic">"{evt.notes}"</p>}
                      </div>
                    </div>

                    {/* Dynamic Action Buttons */}
                    <div className="flex sm:flex-col items-center sm:items-stretch gap-2">
                      <a
                        href={generateGoogleCalendarUrl({
                          title: evt.title,
                          date: evt.date,
                          time: cleanTimeForCalendar(evt.time),
                          description: evt.notes,
                          location: evt.location
                        })}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center justify-center gap-1.5 px-3 py-2 border border-slate-200 rounded-xl text-xs font-bold text-slate-700 bg-white hover:bg-slate-50 hover:border-slate-300 transition-colors shadow-sm"
                      >
                        <CalendarPlus className="w-4 h-4 text-indigo-500" />
                        <span>Add to Google</span>
                      </a>
                      <button
                        onClick={() => downloadICSFile(
                          generateICSContent({
                            title: evt.title,
                            date: evt.date,
                            time: cleanTimeForCalendar(evt.time),
                            description: evt.notes,
                            location: evt.location
                          }),
                          `${evt.type}-${evt.id}.ics`
                        )}
                        className="flex items-center justify-center gap-1.5 px-3 py-2 border border-slate-200 rounded-xl text-xs font-bold text-slate-700 bg-white hover:bg-slate-50 hover:border-slate-300 transition-colors shadow-sm"
                      >
                        <Download className="w-4 h-4 text-emerald-500" />
                        <span>Download .ics</span>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Sidebar strategy sessions booking / status layout (Right 1 col) */}
        <div className="space-y-6">
          
          {/* Active Booking Card or Booking Request Form */}
          {activeBooking ? (
            /* Active Booking Status Card */
            <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-slate-900">Your Booking</h3>
                <span className={`px-2.5 py-1 text-xs font-bold rounded-full ${getStatusBadge(activeBooking.status).bg}`}>
                  {getStatusBadge(activeBooking.status).label}
                </span>
              </div>

              {activeBooking.status === 'pending' && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-4">
                  <div className="flex items-start gap-3">
                    <Clock className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-semibold text-amber-900 text-sm">Request Submitted</p>
                      <p className="text-amber-700 text-xs mt-1">
                        Please wait for us to schedule the time. We'll confirm your session shortly.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {activeBooking.status === 'approved' && (
                <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-4">
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-semibold text-green-900 text-sm">Session Confirmed!</p>
                      <p className="text-green-700 text-xs mt-1">
                        Your strategy session has been scheduled. See the details below.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <div className="space-y-3 text-sm">
                <div className="flex justify-between py-2 border-b border-slate-100">
                  <span className="text-slate-500 text-xs">Requested Date</span>
                  <span className="font-medium text-slate-900 text-xs">{activeBooking.preferred_date}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-slate-100">
                  <span className="text-slate-500 text-xs">Requested Time</span>
                  <span className="font-medium text-slate-900 text-xs">{activeBooking.preferred_time}</span>
                </div>
                {activeBooking.scheduled_date && (
                  <div className="flex justify-between py-2 border-b border-slate-100">
                    <span className="text-slate-500 text-xs">Scheduled Date</span>
                    <span className="font-bold text-green-700 text-xs">{activeBooking.scheduled_date}</span>
                  </div>
                )}
                {activeBooking.scheduled_time && (
                  <div className="flex justify-between py-2 border-b border-slate-100">
                    <span className="text-slate-500 text-xs">Scheduled Time</span>
                    <span className="font-bold text-green-700 text-xs">{activeBooking.scheduled_time}</span>
                  </div>
                )}
                {activeBooking.topic && (
                  <div className="flex justify-between py-2 border-b border-slate-100">
                    <span className="text-slate-500 text-xs">Topic</span>
                    <span className="font-medium text-slate-900 text-xs text-right max-w-[150px] truncate">{activeBooking.topic}</span>
                  </div>
                )}
                {activeBooking.admin_note && (
                  <div className="pt-2">
                    <span className="text-slate-500 text-[11px] block mb-1">Note from team:</span>
                    <p className="bg-slate-50 p-2.5 rounded-lg text-slate-700 text-xs italic">"{activeBooking.admin_note}"</p>
                  </div>
                )}
              </div>

              {activeBooking.meeting_link && (
                <a
                  href={activeBooking.meeting_link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-4 w-full py-2.5 bg-indigo-600 text-white rounded-xl font-bold flex items-center justify-center gap-2 hover:bg-indigo-700 transition-colors text-xs"
                >
                  <Video className="w-4 h-4" />
                  Join Meeting
                  <ExternalLink className="w-3.5 h-3.5" />
                </a>
              )}
            </div>
          ) : (
            /* Booking Form */
            <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-indigo-50 text-indigo-600 rounded-xl flex items-center justify-center flex-shrink-0">
                  <CalendarIcon className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="text-base font-bold text-slate-900">Book a Session</h3>
                  <p className="text-xs text-slate-500">Request a 1-on-1 strategy meeting.</p>
                </div>
              </div>

              {bookingNotification.text && (
                <div
                  className={`p-3 mb-4 rounded-xl border flex items-start gap-2.5 transition-all duration-300 ${
                    bookingNotification.type === 'success'
                      ? 'bg-emerald-50 border-emerald-200 text-emerald-800 animate-fadeIn'
                      : 'bg-rose-50 border-rose-200 text-rose-800 animate-fadeIn'
                  }`}
                >
                  {bookingNotification.type === 'success' ? (
                    <CheckCircle className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" />
                  ) : (
                    <AlertCircle className="w-4 h-4 text-rose-600 flex-shrink-0 mt-0.5" />
                  )}
                  <div className="flex-1 text-[11px] font-semibold leading-relaxed">{bookingNotification.text}</div>
                  <button
                    type="button"
                    onClick={() => setBookingNotification({ type: '', text: '' })}
                    className="text-slate-400 hover:text-slate-600 flex-shrink-0 focus:outline-none transition-colors"
                  >
                    <span className="text-sm font-bold leading-none">&times;</span>
                  </button>
                </div>
              )}

              <form onSubmit={handleSubmitBooking} className="space-y-4">
                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1">Preferred Date *</label>
                  <input
                    type="date"
                    required
                    value={preferredDate}
                    min={todayStr}
                    onChange={(e) => setPreferredDate(e.target.value)}
                    className="w-full px-3 py-2.5 border border-slate-200 rounded-xl text-xs focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1">Preferred Time *</label>
                  <select
                    required
                    value={preferredTime}
                    onChange={(e) => setPreferredTime(e.target.value)}
                    className="w-full px-3 py-2.5 border border-slate-200 rounded-xl text-xs focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 bg-white"
                  >
                    <option value="">Select a time slot</option>
                    {TIME_SLOTS.map(slot => (
                      <option key={slot} value={slot}>{slot}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1">Topic description</label>
                  <textarea
                    value={topic}
                    onChange={(e) => setTopic(e.target.value)}
                    placeholder="e.g. CV optimization, mock interview prep, salary tips..."
                    rows={3}
                    className="w-full px-3 py-2.5 border border-slate-200 rounded-xl text-xs focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 resize-none"
                  />
                </div>

                <button
                  type="submit"
                  disabled={requesting}
                  className="w-full py-2.5 bg-indigo-600 text-white rounded-xl font-bold hover:bg-indigo-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2 text-xs"
                >
                  {requesting ? <Loader2 className="w-4 h-4 animate-spin" /> : <CalendarIcon className="w-4 h-4" />}
                  {requesting ? 'Submitting...' : 'Request Session'}
                </button>
              </form>
            </div>
          )}

          {/* Upcoming Schedule */}
          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6">
            <h3 className="text-base font-bold text-slate-800 mb-4 flex items-center gap-2">
              <Clock className="w-4 h-4 text-indigo-500" />
              <span>Upcoming Events</span>
            </h3>
            
            {upcomingEvents.length === 0 ? (
              <p className="text-xs text-slate-400 italic text-center py-2">No upcoming events scheduled.</p>
            ) : (
              <div className="space-y-3">
                {upcomingEvents.map(evt => (
                  <button 
                    key={evt.id} 
                    onClick={() => setSelectedDate(new Date(evt.date))}
                    className="group w-full text-left relative flex items-start gap-3 pl-3 before:absolute before:left-0 before:top-1 before:bottom-1 before:w-1 before:rounded-full before:bg-indigo-500 hover:before:bg-indigo-600 transition-colors"
                  >
                    <div className="space-y-0.5 flex-1 min-w-0">
                      <h4 className="text-xs font-bold text-slate-800 truncate group-hover:text-indigo-600 transition-colors">
                        {evt.title}
                      </h4>
                      <p className="text-[10px] text-slate-500 flex items-center gap-1">
                        <Clock className="w-3 h-3 text-slate-400" />
                        <span>{formatEventDateTime(evt.date, cleanTimeForCalendar(evt.time))}</span>
                      </p>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Past Sessions History */}
          {pastBookings.length > 0 && (
            <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6">
              <h3 className="text-base font-bold text-slate-900 mb-4">Past Sessions</h3>
              <div className="space-y-3 max-h-52 overflow-y-auto pr-1">
                {pastBookings.map(b => {
                  const isExpired = b.status === 'expired' || checkBookingExpired(b);
                  const effectiveStatus = isExpired ? 'expired' : b.status;
                  const isRejected = b.status === 'rejected';

                  return (
                    <div key={b.id} className={`p-2.5 rounded-xl text-xs ${
                      isRejected 
                        ? 'bg-rose-50 border border-rose-100' 
                        : isExpired 
                          ? 'bg-orange-50 border border-orange-100 text-orange-900' 
                          : 'bg-slate-50'
                    }`}>
                      <div className="flex items-center justify-between">
                        <div className="min-w-0 flex-1 mr-2">
                          <p className="font-semibold text-slate-900 truncate">{b.scheduled_date || b.preferred_date}</p>
                          <p className="text-[10px] text-slate-500 truncate">{b.topic || 'Strategy Session'}</p>
                        </div>
                        <span className={`px-2 py-0.5 text-[10px] font-bold rounded-full shrink-0 ${getStatusBadge(effectiveStatus).bg}`}>
                          {getStatusBadge(effectiveStatus).label}
                        </span>
                      </div>
                      {isRejected && b.admin_note && (
                        <div className="mt-1.5 pt-1.5 border-t border-rose-100">
                          <p className="text-[10px] text-rose-600 font-medium">Reason: <span className="font-normal italic">{b.admin_note}</span></p>
                        </div>
                      )}
                      {isExpired && (
                        <div className="mt-1.5 pt-1.5 border-t border-orange-100">
                          <p className="text-[10px] text-orange-700 font-semibold">
                            ⚠️ Request expired. Please submit a new preferred date/time.
                          </p>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
